import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { projectsAPI, reportsAPI, usersAPI, adminAPI, filesAPI } from '../services/api';
import axios from 'axios';
import {
  ArrowLeft,
  MapPin,
  DollarSign,
  Calendar,
  User,
  Target,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  UserPlus,
  Building,
  RotateCcw,
  Send,
  Eye,
  Download,
  File,
  Users,
  ShieldCheck,
  BarChart3,
  Loader2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  agent_assigned: { label: 'Agent Assigned', color: 'bg-indigo-100 text-indigo-700' },
  in_progress: { label: 'In Progress', color: 'bg-cyan-100 text-cyan-700' },
  awaiting_agents: { label: 'Awaiting Agent Reports', color: 'bg-purple-100 text-purple-700' },
  one_report_submitted: { label: 'One Report Submitted', color: 'bg-violet-100 text-violet-700' },
  report_submitted: { label: 'Report Submitted', color: 'bg-purple-100 text-purple-700' },
  both_reports_submitted: { label: 'Both Reports Submitted', color: 'bg-indigo-100 text-indigo-700' },
  under_admin_review: { label: 'Under Admin Review', color: 'bg-orange-100 text-orange-700' },
  released_to_donor: { label: 'Released to Donor', color: 'bg-teal-100 text-teal-700' },
  donor_feedback: { label: 'Donor Feedback', color: 'bg-emerald-100 text-emerald-700' },
  revision_required: { label: 'Revision Required', color: 'bg-rose-100 text-rose-700' },
  verification_complete: { label: 'Verification Complete', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' }
};

const REPORT_STATUS_LABELS = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_admin_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  revision_requested: { label: 'Revision Requested', color: 'bg-orange-100 text-orange-700', icon: RotateCcw },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  released: { label: 'Released to Donor', color: 'bg-blue-100 text-blue-700', icon: Send },
  donor_approved: { label: 'Accepted by Donor', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  donor_revision_requested: { label: 'Donor Revision Requested', color: 'bg-rose-100 text-rose-700', icon: RotateCcw },
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [reports, setReports] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [comparison, setComparison] = useState(null);
  const [verificationMode, setVerificationMode] = useState('single');
  const [selectedAgent1, setSelectedAgent1] = useState('');
  const [selectedAgent2, setSelectedAgent2] = useState('');
  const [finalValues, setFinalValues] = useState({});
  const [savingFinal, setSavingFinal] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const isDonor = user?.role === 'donor';
  const isDouble = project?.verification_mode === 'double';

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, reportsRes] = await Promise.all([
        projectsAPI.get(projectId),
        reportsAPI.list({ project_id: projectId }).catch(() => ({ data: [] }))
      ]);
      setProject(projectRes.data);
      setReports(reportsRes.data || []);
      setVerificationMode(projectRes.data?.verification_mode || 'single');

      if (isAdmin) {
        const agentsRes = await usersAPI.listAgents({ status: 'approved' }).catch(() => ({ data: [] }));
        setAgents(agentsRes.data || []);
      }

      // Fetch comparison data for admin or donor
      if ((isAdmin || isDonor) && projectRes.data?.indicators?.length > 0) {
        try {
          const compRes = await projectsAPI.getIndicatorComparison(projectId);
          setComparison(compRes.data);
          // Init final values from existing data
          const existing = {};
          (compRes.data?.final_verified_values || []).forEach(fv => {
            existing[fv.indicator_name] = { value: fv.final_value, method: fv.method };
          });
          setFinalValues(existing);
        } catch {}
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveProject = async () => {
    try {
      await projectsAPI.approve(projectId, verificationMode);
      toast.success(`Project approved as ${verificationMode} verification`);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve project');
    }
  };

  const handleRejectProject = async () => {
    const reason = window.prompt('Enter rejection reason (optional):');
    try {
      await projectsAPI.reject(projectId, reason || '');
      toast.success('Project rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject project');
    }
  };

  const handleCompleteProject = async () => {
    if (!window.confirm('Mark this project as completed? This is a final status.')) return;
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('token');
      await axios.put(`${API}/projects/${projectId}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Project marked as completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete project');
    }
  };

  const handleAssignAgent = async (agentId) => {
    try {
      await projectsAPI.assignAgent(projectId, agentId);
      toast.success('Agent assigned successfully');
      setShowAssignDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign agent');
    }
  };

  const handleAssignDoubleAgents = async () => {
    if (!selectedAgent1 || !selectedAgent2) {
      toast.error('Please select both agents');
      return;
    }
    if (selectedAgent1 === selectedAgent2) {
      toast.error('Cannot assign the same agent twice');
      return;
    }
    try {
      await projectsAPI.assignAgents(projectId, selectedAgent1, selectedAgent2);
      toast.success('Two agents assigned for double verification');
      setShowAssignDialog(false);
      setSelectedAgent1('');
      setSelectedAgent2('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign agents');
    }
  };

  const handleReviewReport = async (action) => {
    if (!selectedReport) return;
    try {
      await reportsAPI.review(selectedReport.report_id, action, feedback);
      const messages = { approve: 'Report approved', request_revision: 'Revision requested', release: 'Report released' };
      toast.success(messages[action] || 'Report updated');
      setShowReviewDialog(false);
      setSelectedReport(null);
      setFeedback('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  const handleApproveBoth = async () => {
    try {
      await adminAPI.approveBoth(projectId, feedback);
      toast.success('Both reports approved');
      setFeedback('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve reports');
    }
  };

  const handleReleaseBoth = async () => {
    try {
      await adminAPI.releaseProjectReports(projectId);
      toast.success('All approved reports released to donor');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release reports');
    }
  };

  const handleSaveFinalValues = async () => {
    setSavingFinal(true);
    try {
      const values = Object.entries(finalValues).map(([name, data]) => ({
        indicator_name: name,
        final_value: data.value,
        method: data.method
      }));
      await adminAPI.setFinalValues(projectId, { final_verified_values: values });
      toast.success('Final verified values saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save final values');
    } finally {
      setSavingFinal(false);
    }
  };

  const updateFinalValue = (indicatorName, method, compIndicator) => {
    let value = '';
    if (method === 'agent_1') value = compIndicator.agent1_value || '';
    else if (method === 'agent_2') value = compIndicator.agent2_value || '';
    else if (method === 'average') {
      try {
        const v1 = parseFloat(compIndicator.agent1_value);
        const v2 = parseFloat(compIndicator.agent2_value);
        value = ((v1 + v2) / 2).toFixed(1);
      } catch { value = ''; }
    } else if (method === 'manual') {
      value = finalValues[indicatorName]?.value || '';
    }
    setFinalValues(prev => ({ ...prev, [indicatorName]: { value, method } }));
  };

  const getStatusBadge = (status) => {
    const s = STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
    return <span className={`px-3 py-1 rounded-full text-sm font-medium ${s.color}`}>{s.label}</span>;
  };

  const getReportStatusBadge = (status) => {
    const s = REPORT_STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: FileText };
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>
        <Icon className="w-3 h-3" />{s.label}
      </span>
    );
  };

  const getBackPath = () => {
    if (isAdmin) return '/admin/projects';
    if (isAgent) return '/agent';
    if (isDonor) return '/donor';
    return '/';
  };

  const handleExportPDF = async () => {
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project_${projectId}_report.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-900">Project not found</h2>
        <Button className="mt-4" onClick={() => navigate(getBackPath())}>Go Back</Button>
      </div>
    );
  }

  const pendingReports = reports.filter(r => r.status === 'pending_admin_review');
  const approvedReports = reports.filter(r => r.status === 'approved');

  return (
    <div className="space-y-6 page-enter" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <Button variant="ghost" className="mb-4" onClick={() => navigate(getBackPath())}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Projects
          </Button>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">{project.title}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {getStatusBadge(project.status)}
            <span className="text-slate-500">{project.organization_name}</span>
            {isDouble && (
              <Badge className="bg-indigo-100 text-indigo-700 gap-1">
                <Users className="w-3 h-3" />Double Verification
              </Badge>
            )}
          </div>
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <Download className="w-4 h-4 mr-2" />Export PDF
            </Button>
            {project.status === 'pending_review' && (
              <div className="flex items-center gap-2">
                <Select value={verificationMode} onValueChange={setVerificationMode}>
                  <SelectTrigger className="w-[180px]" data-testid="verification-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single Verification</SelectItem>
                    <SelectItem value="double">Double Verification</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" className="text-red-600" onClick={handleRejectProject}>Reject</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApproveProject} data-testid="approve-project-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" />Approve
                </Button>
              </div>
            )}
            {['approved', 'agent_assigned'].includes(project.status) && (
              <Button onClick={() => setShowAssignDialog(true)} data-testid="assign-agent-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                {isDouble ? 'Assign 2 Agents' : 'Assign Agent'}
              </Button>
            )}
            {/* Sequential second agent assignment - after first report submitted */}
            {!isDouble && project.assigned_agent_ids?.length === 1 && reports.length > 0 &&
              !['pending_review', 'approved', 'completed', 'rejected'].includes(project.status) && (
              <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => setShowAssignDialog(true)} data-testid="assign-second-agent-btn">
                <UserPlus className="w-4 h-4 mr-2" />Assign Second Agent
              </Button>
            )}
            {/* Batch actions for double verification */}
            {isDouble && pendingReports.length >= 2 && (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApproveBoth} data-testid="approve-both-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />Approve Both
              </Button>
            )}
            {isDouble && approvedReports.length >= 2 && (
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleReleaseBoth} data-testid="release-both-btn">
                <Send className="w-4 h-4 mr-2" />Release Both
              </Button>
            )}
            {approvedReports.length === 1 && !isDouble && (
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleReleaseBoth} data-testid="release-btn">
                <Send className="w-4 h-4 mr-2" />Release to Donor
              </Button>
            )}
            {project.status !== 'completed' && project.status !== 'pending_review' && project.status !== 'rejected' && (
              <Button className="bg-green-700 hover:bg-green-800" onClick={handleCompleteProject} data-testid="mark-complete-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" />Mark Complete
              </Button>
            )}
          </div>
        )}

        {(isDonor || isAgent) && (
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-2" />Export PDF
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="stat-card">
            <CardHeader><CardTitle className="text-lg font-['Manrope']">Project Description</CardTitle></CardHeader>
            <CardContent><p className="text-slate-600 whitespace-pre-wrap">{project.description || 'No description'}</p></CardContent>
          </Card>

          {/* Goals & Indicators */}
          {(project.goals || project.indicators?.length > 0) && (
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Target className="w-5 h-5" />Goals & Indicators
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.goals && (
                  <div>
                    <Label className="text-sm text-slate-500">Project Goals</Label>
                    <p className="text-slate-700 mt-1">{project.goals}</p>
                  </div>
                )}
                {project.indicators?.length > 0 && (
                  <div>
                    <Label className="text-sm text-slate-500">Key Performance Indicators</Label>
                    <div className="mt-2 space-y-2">
                      {project.indicators.map((ind, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <span className="font-medium text-slate-700">{ind.name}</span>
                            {ind.description && <p className="text-xs text-slate-500">{ind.description}</p>}
                          </div>
                          <Badge variant="secondary">Target: {ind.target_value || ind.target}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Admin Indicator Comparison for Double Verification */}
          {isAdmin && isDouble && comparison?.indicators?.length > 0 && (
            <Card className="stat-card border-indigo-200" data-testid="admin-double-comparison">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />Double Verification Comparison
                </CardTitle>
                <CardDescription>
                  Discrepancy threshold: {comparison.discrepancy_threshold}%
                  {comparison.agent_names?.length >= 2 && ` | Agent 1: ${comparison.agent_names[0]} | Agent 2: ${comparison.agent_names[1]}`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-3 font-medium text-slate-700">Indicator</th>
                        <th className="text-left p-3 font-medium text-slate-700">Planned Value</th>
                        <th className="text-left p-3 font-medium text-slate-700">Agent 1</th>
                        <th className="text-left p-3 font-medium text-slate-700">Agent 2</th>
                        <th className="text-left p-3 font-medium text-slate-700">Discrepancy</th>
                        <th className="text-left p-3 font-medium text-slate-700">Final Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.indicators.map((ci, idx) => (
                        <tr key={idx} className={`border-b border-slate-100 last:border-0 ${ci.discrepancy_level === 'high' ? 'bg-red-50' : ci.discrepancy_level === 'moderate' ? 'bg-amber-50' : ''}`}>
                          <td className="p-3 font-medium text-slate-900">{ci.indicator_name}</td>
                          <td className="p-3 text-slate-600">{ci.donor_value || '-'}</td>
                          <td className="p-3 text-slate-700">{ci.agent1_value || <span className="text-slate-400 italic">Pending</span>}</td>
                          <td className="p-3 text-slate-700">{ci.agent2_value || <span className="text-slate-400 italic">Pending</span>}</td>
                          <td className="p-3">
                            {ci.discrepancy_percent !== null ? (
                              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                                ci.discrepancy_level === 'mismatch' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {ci.discrepancy_level === 'mismatch' && <AlertTriangle className="w-3 h-3" />}
                                {ci.discrepancy_level === 'mismatch' ? `Mismatch (${ci.discrepancy_percent}%)` : `Consistent (${ci.discrepancy_percent}%)`}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <Select
                                value={finalValues[ci.indicator_name]?.method || ''}
                                onValueChange={(v) => updateFinalValue(ci.indicator_name, v, ci)}
                              >
                                <SelectTrigger className="h-8 text-xs w-[110px]">
                                  <SelectValue placeholder="Select..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="agent_1">Agent 1</SelectItem>
                                  <SelectItem value="agent_2">Agent 2</SelectItem>
                                  <SelectItem value="average">Average</SelectItem>
                                  <SelectItem value="manual">Manual</SelectItem>
                                </SelectContent>
                              </Select>
                              {finalValues[ci.indicator_name]?.method === 'manual' ? (
                                <Input
                                  className="h-8 text-xs w-20"
                                  value={finalValues[ci.indicator_name]?.value || ''}
                                  onChange={(e) => setFinalValues(prev => ({ ...prev, [ci.indicator_name]: { value: e.target.value, method: 'manual' } }))}
                                />
                              ) : (
                                <span className="text-xs font-medium text-indigo-600 min-w-[40px]">
                                  {finalValues[ci.indicator_name]?.value || '-'}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveFinalValues} disabled={savingFinal} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-final-values-btn">
                    {savingFinal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Final Values
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Admin Single Verification Comparison */}
          {isAdmin && !isDouble && comparison?.indicators?.length > 0 && reports.some(r => r.indicator_verification?.length > 0) && (
            <Card className="stat-card" data-testid="admin-single-comparison">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />Indicator Verification
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-3 font-medium text-slate-700">Indicator</th>
                        <th className="text-left p-3 font-medium text-slate-700">Planned Value</th>
                        <th className="text-left p-3 font-medium text-slate-700">Verified Value</th>
                        <th className="text-left p-3 font-medium text-slate-700">Agent Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.indicators.map((ci, idx) => (
                        <tr key={idx} className="border-b border-slate-100 last:border-0">
                          <td className="p-3 font-medium text-slate-900">{ci.indicator_name}</td>
                          <td className="p-3 text-slate-600">{ci.donor_value || '-'}</td>
                          <td className="p-3 font-medium">{ci.agent1_value || <span className="text-slate-400 italic">Pending</span>}</td>
                          <td className="p-3 text-slate-500 text-xs">{ci.agent1_notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Budget Breakdown */}
          {project.budget_breakdown && (
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />Budget Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg text-sm">{project.budget_breakdown}</pre>
              </CardContent>
            </Card>
          )}

          {/* Reports Section */}
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <FileText className="w-5 h-5" />Field Reports
              </CardTitle>
              <CardDescription>{reports.length} report(s) for this project</CardDescription>
            </CardHeader>
            <CardContent>
              {reports.length > 0 ? (
                <div className="space-y-4">
                  {reports.map((report) => (
                    <div key={report.report_id} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {getReportStatusBadge(report.status)}
                            {isDouble && report.agent_slot && (
                              <Badge variant="outline" className="text-xs">Agent {report.agent_slot}</Badge>
                            )}
                            <h4 className="font-medium text-slate-900">{report.title}</h4>
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-2">{report.content}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>by {report.agent_name}</span>
                            <span>{new Date(report.created_at).toLocaleDateString()}</span>
                            {report.revision_count > 0 && <span className="text-amber-600">Revision #{report.revision_count}</span>}
                          </div>

                          {/* Admin feedback - visible to admin and agent only */}
                          {(isAgent || isAdmin) && report.admin_feedback && (
                            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <p className="text-sm text-amber-800"><strong>Admin Feedback:</strong> {report.admin_feedback}</p>
                            </div>
                          )}

                          {/* Donor feedback - visible to admin and agent */}
                          {(isAdmin || isAgent) && report.donor_feedback && (
                            <div className={`mt-3 p-3 rounded-lg border ${report.status === 'donor_approved' ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}>
                              <p className={`text-sm ${report.status === 'donor_approved' ? 'text-emerald-800' : 'text-blue-800'}`}>
                                <strong>Donor Feedback:</strong> {report.donor_feedback}
                              </p>
                            </div>
                          )}

                          {/* Evidence files for admin */}
                          {isAdmin && report.evidence_file_ids?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-slate-500 font-medium mb-1">Evidence Files ({report.evidence_file_ids.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {report.evidence_file_ids.map((fileId) => (
                                  <a key={fileId} href={filesAPI.view(fileId)} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-slate-100 rounded hover:bg-slate-200 transition-colors text-slate-700">
                                    <File className="w-3 h-3" />{fileId.substring(0, 12)}...
                                    <Download className="w-3 h-3" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 flex-shrink-0 flex-wrap">
                          {isDonor && ['released', 'donor_approved', 'donor_revision_requested'].includes(report.status) && (
                            <Button size="sm" onClick={() => navigate(`/donor/reports/${report.report_id}`)} data-testid={`view-report-${report.report_id}`}>
                              <Eye className="w-4 h-4 mr-1" />View
                            </Button>
                          )}
                          {isAdmin && report.status === 'pending_admin_review' && (
                            <Button size="sm" onClick={() => { setSelectedReport(report); setShowReviewDialog(true); }} data-testid={`review-report-${report.report_id}`}>
                              <Eye className="w-4 h-4 mr-1" />Review
                            </Button>
                          )}
                          {isAdmin && report.status === 'approved' && (
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => {
                              try { await reportsAPI.review(report.report_id, 'release', ''); toast.success('Report released'); fetchData(); }
                              catch { toast.error('Failed to release'); }
                            }} data-testid={`release-report-${report.report_id}`}>
                              <Send className="w-4 h-4 mr-1" />Release
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-2" />
                  <p>No reports submitted yet</p>
                  {isAgent && ProjectDetailPage.AGENT_ACTIVE_STATUSES?.includes(project.status) && (
                    <Button className="mt-4" asChild><Link to="/agent/submit-report">Submit Report</Link></Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="stat-card">
            <CardHeader><CardTitle className="text-lg font-['Manrope']">Project Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center"><Building className="w-5 h-5 text-slate-500" /></div>
                <div><p className="text-xs text-slate-500">Organization</p><p className="font-medium text-slate-900">{project.organization_name}</p></div>
              </div>
              {(project.country || project.region) && (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-slate-500" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Location</p>
                    <p className="font-medium text-slate-900">
                      {[project.street_address, project.city, project.region, project.country].filter(Boolean).join(', ')}
                    </p>
                    {project.postal_code && <p className="text-xs text-slate-500 mt-0.5">Postal Code: {project.postal_code}</p>}
                  </div>
                </div>
              )}
              {project.budget && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center"><DollarSign className="w-5 h-5 text-slate-500" /></div>
                  <div><p className="text-xs text-slate-500">Budget</p><p className="font-medium text-slate-900">${project.budget.toLocaleString()}</p></div>
                </div>
              )}
              {(project.start_date || project.end_date) && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center"><Calendar className="w-5 h-5 text-slate-500" /></div>
                  <div><p className="text-xs text-slate-500">Timeline</p><p className="font-medium text-slate-900">{project.start_date && new Date(project.start_date).toLocaleDateString()}{project.start_date && project.end_date && ' - '}{project.end_date && new Date(project.end_date).toLocaleDateString()}</p></div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-slate-500" /></div>
                <div>
                  <p className="text-xs text-slate-500">Verification Mode</p>
                  <p className="font-medium text-slate-900">{isDouble ? 'Double Agent' : 'Single Agent'}</p>
                </div>
              </div>
              {project.verification_type && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-slate-500" /></div>
                  <div><p className="text-xs text-slate-500">Verification Type</p><p className="font-medium text-slate-900">{project.verification_type}</p></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assigned Agent(s) */}
          {(project.assigned_agent_names?.length > 0 || project.assigned_agent_name) && (
            <Card className="stat-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <User className="w-5 h-5" />{isDouble ? 'Assigned Agents' : 'Assigned Agent'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {project.assigned_agent_names?.length > 0 ? (
                  project.assigned_agent_names.map((name, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${idx === 0 ? 'bg-emerald-100' : 'bg-blue-100'} rounded-full flex items-center justify-center`}>
                        <User className={`w-5 h-5 ${idx === 0 ? 'text-emerald-600' : 'text-blue-600'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{name}</p>
                        <p className={`text-sm ${idx === 0 ? 'text-emerald-600' : 'text-blue-600'}`}>Agent {idx + 1}</p>
                      </div>
                    </div>
                  ))
                ) : project.assigned_agent_name && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{project.assigned_agent_name}</p>
                      <p className="text-sm text-emerald-600">Field Agent</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {project.donor_name && !isDonor && (
            <Card className="stat-card">
              <CardHeader><CardTitle className="text-lg font-['Manrope'] flex items-center gap-2"><User className="w-5 h-5" />Project Donor</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-blue-600" /></div>
                  <div><p className="font-medium text-slate-900">{project.donor_name}</p><p className="text-sm text-blue-600">Donor</p></div>
                </div>
              </CardContent>
            </Card>
          )}

          {project.document_ids?.length > 0 && (
            <Card className="stat-card">
              <CardHeader><CardTitle className="text-lg font-['Manrope'] flex items-center gap-2"><File className="w-5 h-5" />Documents</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {project.document_ids.map((docId, idx) => (
                    <a key={docId} href={filesAPI.view(docId)} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors">
                      <span className="text-sm text-slate-600">Document {idx + 1}</span>
                      <Download className="w-4 h-4 text-slate-400" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="stat-card">
            <CardHeader><CardTitle className="text-lg font-['Manrope']">Progress</CardTitle></CardHeader>
            <CardContent>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-slate-500">Completion</span>
                <span className="font-medium text-slate-700">{project.progress || 0}%</span>
              </div>
              <div className="progress-bar h-3">
                <div className="progress-bar-fill" style={{ width: `${project.progress || 0}%` }}></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assign Agent Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {isDouble ? 'Assign Two Agents (Double Verification)' : 'Assign Agent to Project'}
            </DialogTitle>
            <DialogDescription>
              {isDouble ? 'Select two different agents for independent verification.' : 'Select the most suitable agent.'}
            </DialogDescription>
          </DialogHeader>

          {isDouble ? (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Agent 1</Label>
                <Select value={selectedAgent1} onValueChange={setSelectedAgent1}>
                  <SelectTrigger data-testid="select-agent-1"><SelectValue placeholder="Select Agent 1" /></SelectTrigger>
                  <SelectContent>
                    {agents.filter(a => a.user_id !== selectedAgent2).map(a => (
                      <SelectItem key={a.user_id} value={a.user_id}>{a.name} {a.country ? `(${a.country})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Agent 2</Label>
                <Select value={selectedAgent2} onValueChange={setSelectedAgent2}>
                  <SelectTrigger data-testid="select-agent-2"><SelectValue placeholder="Select Agent 2" /></SelectTrigger>
                  <SelectContent>
                    {agents.filter(a => a.user_id !== selectedAgent1).map(a => (
                      <SelectItem key={a.user_id} value={a.user_id}>{a.name} {a.country ? `(${a.country})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleAssignDoubleAgents} data-testid="confirm-assign-agents-btn">
                <Users className="w-4 h-4 mr-2" />Assign Both Agents
              </Button>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {agents.length > 0 ? agents.map((agent) => (
                <div key={agent.user_id} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors" data-testid={`agent-option-${agent.user_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-emerald-600" /></div>
                        <div><p className="font-medium text-slate-900">{agent.name}</p><p className="text-sm text-slate-500">{agent.email}</p></div>
                      </div>
                      <div className="ml-13 space-y-1 text-sm">
                        {agent.agent_profile?.field_of_expertise && <p className="text-emerald-600"><strong>Expertise:</strong> {agent.agent_profile.field_of_expertise}</p>}
                        {agent.country && <p className="text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{agent.country}</p>}
                      </div>
                    </div>
                    <Button onClick={() => handleAssignAgent(agent.user_id)} className="bg-emerald-600 hover:bg-emerald-700">
                      <UserPlus className="w-4 h-4 mr-2" />Assign
                    </Button>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p className="font-medium">No approved agents available</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={(open) => { setShowReviewDialog(open); if (!open) { setSelectedReport(null); setFeedback(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Review Report</DialogTitle>
            <DialogDescription>Review the submitted report and take action</DialogDescription>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-sm text-slate-500">Report Title</Label><p className="font-medium">{selectedReport.title}</p></div>
                <div><Label className="text-sm text-slate-500">Submitted by</Label><p>{selectedReport.agent_name}{isDouble && selectedReport.agent_slot ? ` (Agent ${selectedReport.agent_slot})` : ''}</p></div>
              </div>
              <div>
                <Label className="text-sm text-slate-500">Content</Label>
                <div className="mt-1 p-4 bg-slate-50 rounded-lg text-slate-700 whitespace-pre-wrap max-h-[200px] overflow-y-auto">{selectedReport.content}</div>
              </div>
              {selectedReport.findings && (<div><Label className="text-sm text-slate-500">Findings</Label><p className="mt-1 p-3 bg-slate-50 rounded-lg text-slate-700">{selectedReport.findings}</p></div>)}
              {selectedReport.recommendations && (<div><Label className="text-sm text-slate-500">Recommendations</Label><p className="mt-1 p-3 bg-slate-50 rounded-lg text-slate-700">{selectedReport.recommendations}</p></div>)}

              {/* Indicator verification in this report */}
              {selectedReport.indicator_verification?.length > 0 && (
                <div>
                  <Label className="text-sm text-slate-500 mb-2 block">Indicator Verification</Label>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 border-b"><th className="text-left p-2 text-xs font-medium text-slate-600">Indicator</th><th className="text-left p-2 text-xs font-medium text-slate-600">Target</th><th className="text-left p-2 text-xs font-medium text-slate-600">Verified</th><th className="text-left p-2 text-xs font-medium text-slate-600">Notes</th></tr></thead>
                      <tbody>
                        {selectedReport.indicator_verification.map((iv, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="p-2 font-medium text-slate-900 text-xs">{iv.indicator_name}</td>
                            <td className="p-2 text-slate-600 text-xs">{iv.donor_value || '-'}</td>
                            <td className="p-2 text-xs font-medium">{iv.verified_value || <span className="text-slate-400 italic">-</span>}</td>
                            <td className="p-2 text-slate-500 text-xs">{iv.agent_notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Evidence files */}
              {selectedReport.evidence_file_ids?.length > 0 && (
                <div>
                  <Label className="text-sm text-slate-500">Evidence Files ({selectedReport.evidence_file_ids.length})</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedReport.evidence_file_ids.map((fid) => (
                      <a key={fid} href={filesAPI.view(fid)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs px-3 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                        <File className="w-3 h-3" />{fid.substring(0, 15)}...<Download className="w-3 h-3 ml-1" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="feedback">Feedback / Comments</Label>
                <Textarea id="feedback" placeholder="Add feedback..." value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3} className="mt-1" />
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" className="flex-1 text-orange-600 hover:bg-orange-50 border-orange-200" onClick={() => handleReviewReport('request_revision')}>
                  <RotateCcw className="w-4 h-4 mr-2" />Request Revision
                </Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleReviewReport('approve')}>
                  <CheckCircle2 className="w-4 h-4 mr-2" />Approve Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
